import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface ProfileInfo {
  id: string | number;
  name: string;
  type: 'self' | 'dependant';
  relationship?: string | null;
  avatar?: string | null;
}

interface ProfileContextType {
  profiles: ProfileInfo[];
  activeProfile: ProfileInfo | null;
  isProfileSelected: boolean;
  switchProfile: (profile: ProfileInfo) => void;
  switchToSelf: () => void;
  fetchProfiles: () => Promise<void>;
  showProfileSwitcher: () => void;
  isViewingAsOther: boolean;
  isDependantUser: boolean;
  profilePhoto: string | null;
  setProfilePhoto: (url: string | null) => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [profiles, setProfiles] = useState<ProfileInfo[]>([]);
  const [activeProfile, setActiveProfile] = useState<ProfileInfo | null>(null);
  const [isProfileSelected, setIsProfileSelected] = useState(false);
  const [isDependantUser, setIsDependantUser] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  // Load the logged-in user's photo once authenticated (header avatar, etc.)
  useEffect(() => {
    if (!isAuthenticated) {
      setProfilePhoto(null);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get(API_ENDPOINTS.auth.profile)
      .then((res) => {
        if (!cancelled) setProfilePhoto(res.data?.profile?.profile_photo || null);
      })
      .catch(() => {
        // Photo is non-critical; avatar falls back to initials
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Restore active profile from localStorage on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      const stored = localStorage.getItem('activeProfile');
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as ProfileInfo;
          setActiveProfile(parsed);
          setIsProfileSelected(true);
        } catch {
          localStorage.removeItem('activeProfile');
        }
      }
    }
  }, [isAuthenticated, user]);

  // Reset state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setProfiles([]);
      setActiveProfile(null);
      setIsProfileSelected(false);
    }
  }, [isAuthenticated]);

  const fetchProfiles = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await axiosInstance.get(API_ENDPOINTS.dependants.switchableProfiles);
      const data = response.data;
      const fetchedProfiles: ProfileInfo[] = Array.isArray(data) ? data : (data.profiles || []);
      setProfiles(fetchedProfiles);
      setIsDependantUser(!Array.isArray(data) && data.is_dependant_user === true);

      // If only self profile exists, auto-select it
      if (fetchedProfiles.length === 1 && fetchedProfiles[0].type === 'self') {
        const selfProfile = fetchedProfiles[0];
        setActiveProfile(selfProfile);
        setIsProfileSelected(true);
        localStorage.setItem('activeProfile', JSON.stringify(selfProfile));
      } else if (!activeProfile && fetchedProfiles.length > 0) {
        // If no profile is selected and there are multiple, show the switcher
        const stored = localStorage.getItem('activeProfile');
        if (stored) {
          try {
            const parsed = JSON.parse(stored) as ProfileInfo;
            // Verify the stored profile still exists in the fetched list
            const stillExists = fetchedProfiles.some(
              (p) => String(p.id) === String(parsed.id)
            );
            if (stillExists) {
              setActiveProfile(parsed);
              setIsProfileSelected(true);
            } else {
              localStorage.removeItem('activeProfile');
              setIsProfileSelected(false);
            }
          } catch {
            localStorage.removeItem('activeProfile');
            setIsProfileSelected(false);
          }
        } else {
          setIsProfileSelected(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch switchable profiles:', err);
      // Fallback: create a self profile from the user data
      if (user) {
        const selfProfile: ProfileInfo = {
          id: 'self',
          name: user.name || 'You',
          type: 'self',
          relationship: null,
          avatar: null,
        };
        setProfiles([selfProfile]);
        setActiveProfile(selfProfile);
        setIsProfileSelected(true);
        localStorage.setItem('activeProfile', JSON.stringify(selfProfile));
      }
    }
  }, [isAuthenticated, user, activeProfile]);

  // Fetch profiles when user authenticates
  useEffect(() => {
    if (isAuthenticated && user && profiles.length === 0) {
      fetchProfiles();
    }
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchProfile = useCallback((profile: ProfileInfo) => {
    setActiveProfile(profile);
    setIsProfileSelected(true);
    localStorage.setItem('activeProfile', JSON.stringify(profile));
  }, []);

  const switchToSelf = useCallback(() => {
    const selfProfile = profiles.find((p) => p.type === 'self');
    if (selfProfile) {
      switchProfile(selfProfile);
    }
  }, [profiles, switchProfile]);

  const showProfileSwitcher = useCallback(() => {
    setIsProfileSelected(false);
  }, []);

  const isViewingAsOther = activeProfile !== null && activeProfile.type !== 'self';

  const value: ProfileContextType = {
    profiles,
    activeProfile,
    isProfileSelected,
    switchProfile,
    switchToSelf,
    fetchProfiles,
    showProfileSwitcher,
    isViewingAsOther,
    isDependantUser,
    profilePhoto,
    setProfilePhoto,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export default ProfileContext;
